<template>
  <CModal
    :show.sync="isVisible"
    size="lg"
    centered
  >
    <template #header>
      <div>
        <div class="d-flex align-items-center">
          <CIcon name="cipShield01" size="custom-size" width="36" height="36"
                 class="mr-3 icon-circle icon-circle--primary"/>
          <div>
            <h5 class="modal-title">
              <translate translate-context="collaborators.modal">Add role</translate>
            </h5>
          </div>
        </div>
      </div>
      <CButtonClose @click="close"/>
    </template>
    <div class="role-sections">
      <div v-if="assignableRoles.length > 0" class="roles-grid">
        <div
          v-for="role in assignableRoles"
          :key="role.id"
          class="role-card"
          :class="{ 'selected': selectedRoles[String(role.id)] }"
        >
          <div class="d-flex justify-content-between align-items-start mb-1">
            <div class="role-card__check cursor-pointer" @click="selectRole(role)">
              <CIcon name="cipCheckbox" size="custom-size" :width="20" :class="['checkbox-icon', { 'selected': selectedRoles[String(role.id)] }]"/>
            </div>
            <div class="role-name font-weight-medium mr-1">
              {{ role.display_name || role.name }}
              <small class="d-block" v-if="showRealName(role)"> ({{ role.name }})</small>
            </div>
            <div class="d-flex align-items-center" style="gap: 4px;">
              <span
                class="role-info-icon-wrapper"
                @click.stop="$emit('view-permissions', role)"
              >
                <CIcon
                  name="cipInfo"
                  size="custom-size"
                  :hight="18"
                  :width="18"
                  v-if="showInfoIcon"
                  class="role-info-icon text-primary"
                />
              </span>
              <CBadge
                v-if="role.role_type"
                :color="getRoleTypeBadgeColor(role.role_type)"
                class="role-type-badge"
              >
                {{ getRoleTypeLabel(role.role_type) }}
              </CBadge>
            </div>
          </div>
          <div v-if="role.description" class="role-description" v-html="role.description"></div>
        </div>
      </div>

      <div v-else class="text-center text-muted py-4">
        <translate translate-context="collaborators.modal">No roles available to assign</translate>
      </div>
    </div>

    <template #footer>
      <CButton color="secondary" @click="close">
        <translate translate-context="collaborators.modal">Cancel</translate>
      </CButton>
      <CButton color="primary" :disabled="selectedCount === 0" @click="confirmSelection">
        <translate translate-context="collaborators.modal">Add</translate>
      </CButton>
    </template>
  </CModal>
</template>

<script>
export default {
  name: 'RoleSelectionModal',

  props: {
    availableRoles: {
      type: Array,
      required: true
    },
    scopeType: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      isVisible: false,
      title: '',
      currentRoles: [],
      selectedRoles: {},
      isInheritedUser: false,
      collaboratorId: null,
      isInviteMode: false,
      callback: null
    }
  },

  computed: {
    // Ruoli assegnabili: in modalità invito mostra tutti, altrimenti esclude quelli già assegnati
    assignableRoles() {
      if (this.isInviteMode) {
        return this.availableRoles
      }
      const currentRoleIds = new Set(this.currentRoles.map(r => r.id))
      return this.availableRoles.filter(role => !currentRoleIds.has(role.id))
    },

    selectedCount() {
      return Object.values(this.selectedRoles).filter(v => v === true).length
    },
    isAdvancedUser() {
      return this.hasPerm('collaborator.advanced')
    },
    showInfoIcon() {
      return this.isAdvancedUser
    }
  },

  methods: {
    showRealName(role) {
      return this.isAdvancedUser && role.display_name && role.display_name !== role.name
    },

    show(options) {
      this.title = options.title || this.$pgettext('collaborators.modal', 'Add role')
      this.currentRoles = options.currentRoles || options.roles || []
      this.isInheritedUser = options.isInheritedUser || false
      this.collaboratorId = options.collaboratorId || null
      this.callback = options.callback || null

      // Se c'è un callback ma non un collaboratorId, siamo in modalità invito
      this.isInviteMode = !this.collaboratorId && !!this.callback

      // Reset selezione
      this.$set(this, 'selectedRoles', {})

      if (options.selectedRoles) {
        Object.entries(options.selectedRoles).forEach(([roleId, value]) => {
          this.$set(this.selectedRoles, String(roleId), value)
        })
      }

      this.isVisible = true

      // Pre-select if only one role available
      this.$nextTick(() => {
        if (this.assignableRoles.length === 1 && this.selectedCount === 0) {
          this.$set(this.selectedRoles, String(this.assignableRoles[0].id), true)
        }
      })
    },

    hide() {
      this.isVisible = false
    },

    close() {
      this.hide()
    },

    selectRole(role) {
      const roleIdStr = String(role.id)
      const current = this.selectedRoles[roleIdStr] === true
      this.$set(this.selectedRoles, roleIdStr, !current)
    },

    getRoleTypeLabel(roleType) {
      const labels = {
        'global': this.$pgettext('collaborators.roles', 'Global'),
        'contract_manager': this.$pgettext('collaborators.roles', 'Contract Mgr'),
        'pica_service': this.$pgettext('collaborators.roles', 'Service')
      }
      return labels[roleType] || roleType
    },

    getRoleTypeBadgeColor(roleType) {
      const colors = {
        'global': 'danger',
        'contract_manager': 'warning',
        'pica_service': 'info'
      }
      return colors[roleType] || 'secondary'
    },

    confirmSelection() {
      const selectedIds = Object.entries(this.selectedRoles)
        .filter(([, v]) => v === true)
        .map(([id]) => parseInt(id))

      if (this.callback) {
        this.callback(this.selectedRoles)
      } else if (this.collaboratorId && selectedIds.length > 0) {
        this.$emit('assign-roles', this.collaboratorId, selectedIds)
      }
      this.hide()
    }
  }
}
</script>

<style scoped lang="scss">
.role-sections {
  max-height: 60vh;
  overflow-y: auto;
}

.roles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 12px;
  padding: 2px;
}

.role-card {
  border: 2px solid #d8dbe0;
  border-radius: 8px;
  padding: 12px;
  background-color: #fff;
}

.role-card.selected {
  border-color: #321fdb;
  background-color: #e7f1ff;
}

.role-name {
  flex: 1;
}

.role-description {
  font-size: 12px;
  color: #768192;
  margin-top: 4px;
  line-height: 1.4;
}

.role-description ::v-deep p {
  margin: 0;
}

.role-type-badge {
  font-size: 10px;
  padding: 2px 6px;
  flex-shrink: 0;
  text-transform: uppercase;
  font-weight: 600;
}

.role-info-icon-wrapper {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  flex-shrink: 0;
}

.role-info-icon-wrapper:hover .role-info-icon {
  color: #321fdb;
}
</style>
